import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import type { ImpactPlusReport } from '../entities/report/schemas/ImpactPlusReport.schema';
import type { UseCase } from '../entities/useCaseChat/UseCase';
import { getUseCaseService } from '../services/UseCaseService';
import AuthStore from './AuthStore';
import { DashboardStore } from './DashboardStore';
import UseCaseChatStore from './UseCaseChatStore';

export interface IImpactPlusReportState {
  impactPlusReport: ImpactPlusReport | null;
  useCase: UseCase | null;
  isLoading: boolean;
  loadImpactPlusReport: (reportId: string) => Promise<ImpactPlusReport>;
  setImpactPlusReport: (report: ImpactPlusReport | null) => void;
  generateImpactPlusReport: (useCaseChatId: string) => Promise<{ jobId: string }>;
}

export class ImpactPlusReportStore {
  static useImpactPlusReportStore = create<IImpactPlusReportState>()(
    devtools(
      subscribeWithSelector(
        (set) => ({
          impactPlusReport: null,
          useCase: null,
          isLoading: false,

          async loadImpactPlusReport(reportId: string): Promise<ImpactPlusReport> {
            set({ isLoading: true, useCase: null });
            try {
              const useCaseService = getUseCaseService();
              const report = await useCaseService.getImpactPlusReport(reportId);
              set({ impactPlusReport: report });

              // useCaseChatId can be "[object Object]" due to a backend serialization bug.
              // Fall back to dependencies[].ref_useCaseChat which always contains the real ID.
              const useCaseChatId =
                report.useCaseChatId && report.useCaseChatId !== '[object Object]'
                  ? report.useCaseChatId
                  : report.dependencies?.find((d) => d.ref_useCaseChat)?.ref_useCaseChat ?? '';

              if (useCaseChatId) {
                useCaseService
                  .getUseCaseById(useCaseChatId)
                  .then((useCaseChat) => {
                    set({ useCase: useCaseChat.useCase });
                  })
                  .catch((err) => {
                    console.error('Error loading UseCase for Impact+ report:', err);
                  });
              }

              set({ isLoading: false });
              return report;
            } catch (error) {
              console.error('Error loading Impact+ report:', error);
              set({ impactPlusReport: null, useCase: null, isLoading: false });
              throw new Error('Unable to load Impact+ report');
            }
          },

          setImpactPlusReport: (report: ImpactPlusReport | null) => {
            set({ impactPlusReport: report, useCase: null });
          },

          async generateImpactPlusReport(useCaseChatId: string): Promise<{ jobId: string }> {
            try {
              const useCaseService = getUseCaseService();
              const job = await useCaseService.generateImpactPlusReport(useCaseChatId);

              await AuthStore.useAuthStore.getState().getLicense().catch((error) => {
                console.error('Error updating license after Impact+ report generation:', error);
              });

              UseCaseChatStore.useUseCaseChatStore
                .getState()
                .getUseCaseChatById(useCaseChatId)
                .catch((error) => {
                  console.error('Error refreshing use case chat after Impact+ report generation:', error);
                });

              DashboardStore.useDashboardStore.getState().loadUseCaseBriefs(true);

              return { jobId: job.jobId };
            } catch (error) {
              console.error('Error generating Impact+ report:', error);
              const message = error instanceof Error && error.message ? error.message : 'Unable to generate Impact+ report';
              throw new Error(message);
            }
          },
        })
      ),
      { name: 'ImpactPlusReportStore' }
    )
  );
}

export default ImpactPlusReportStore;
